import { FC, useEffect } from 'react';
import { DatePicker, Form, Input, InputNumber, Select, Switch } from 'antd';
import cn from 'classnames';
import { SendReportPolicy } from '../../../models/enums/SendreportPolicy';
import {
  getQuestionnaires,
  selectQuestionnaires,
} from '../../../store/reducers/QuestionnaireSlice';
import {
  getSipAccounts,
  selectSipAccounts,
} from '../../../store/reducers/SIPAccountSlice';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import styles from './style.module.css';

const { RangePicker } = DatePicker;

interface Props {
  showCampaignSettings: boolean;
}

const CampaignSettings: FC<Props> = ({ showCampaignSettings }: Props) => {
  const dispatch = useAppDispatch();

  const sipAccounts = useAppSelector(selectSipAccounts);
  const questionnaires = useAppSelector(selectQuestionnaires);

  useEffect(() => {
    dispatch(getSipAccounts());
    dispatch(getQuestionnaires());
  }, [dispatch]);

  return (
    <div
      className={cn(
        showCampaignSettings ? styles.blockContainer : styles.invisible,
      )}
    >
      <Form.Item
        label="Название кампании:"
        name={'name'}
        rules={[
          {
            required: true,
            message: 'Необходимо указать название кампании.',
            type: 'string',
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Длительность кампании:"
        name="callDateTime"
        rules={[
          {
            required: true,
            message: 'Необходимо указать длительность кампании',
            type: 'array',
          },
        ]}
      >
        <RangePicker
          className={styles.rangePicker}
          format={'DD.MM.YYYY'}
          //disabledDate={(current) => current < dayjs().add(-1, "day")}
        />
      </Form.Item>

      <Form.Item
        label="Максимум попыток:"
        name="maxTries"
        rules={[
          {
            type: 'number',
            min: 0,
            max: 10,
          },
        ]}
        initialValue={3}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item
        label="Перезвонить через, минут:"
        name="retryInterval"
        rules={[
          {
            type: 'number',
            min: 0,
          },
        ]}
        initialValue={0}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item
        label="Количество успешных вызовов:"
        name="maxSuccessfulCalls"
        rules={[
          {
            type: 'number',
            min: 0,
          },
        ]}
        initialValue={0}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item
        label="Одновременных звонков:"
        name="maxThreads"
        rules={[
          {
            type: 'number',
            min: 0,
          },
        ]}
        initialValue={1}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item
        label="Основной аккаунт SIP:"
        name={'dialAccountId'}
        rules={[
          {
            required: true,
            message: 'Необходимо выбрать SIP аккаунт.',
            type: 'number',
          },
        ]}
      >
        <Select
          className={styles.blockInput}
          defaultActiveFirstOption
          allowClear
          options={sipAccounts.map((x) => ({ value: x.id, label: x.name }))}
        />
      </Form.Item>

      <Form.Item
        label="Аккаунт SIP для соединения c оператором:"
        name={'operatorAccountId'}
        rules={[
          {
            required: true,
            message: 'Необходимо выбрать SIP аккаунт.',
            type: 'number',
          },
        ]}
      >
        <Select className={styles.blockInput} allowClear>
          {sipAccounts.map((account) => (
            <Select.Option key={account.id} value={account.id}>
              {account.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Отправка отчётов:"
        name={'sendReportPolicy'}
        rules={[
          {
            required: true,
            message: 'Необходимо выбрать тип отправки отчётов.',
            type: 'number',
          },
        ]}
      >
        <Select
          className={styles.blockInput}
          allowClear
          options={[
            { value: SendReportPolicy.None, label: 'Не отправлять' },
            { value: SendReportPolicy.Daily, label: 'Ежедневно' },
            { value: SendReportPolicy.Weekly, label: 'Еженедельно' },
            { value: SendReportPolicy.OnCompletion, label: 'По завершении' },
          ]}
        ></Select>
      </Form.Item>

      <Form.Item
        label="Номер оператора:"
        name={'phone'}
        rules={[{ type: 'string' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Выбранная анкета:"
        name={'selectedQuestionnaire'}
        rules={[
          {
            required: true,
            message: 'Необходимо выбрать анкету.',
            type: 'number',
          },
        ]}
      >
        <Select className={styles.blockInput} allowClear>
          {questionnaires.map((questionnarie) => (
            <Select.Option key={questionnarie.id} value={questionnarie.id}>
              {questionnarie.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Подтверждать ФИО:"
        name={'identifyByName'}
        valuePropName="checked"
        rules={[
          {
            type: 'boolean',
          },
        ]}
        initialValue={false}
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label="Подтверждать возможность говорить:"
        name={'askIfReadyToTalk'}
        valuePropName="checked"
        rules={[
          {
            type: 'boolean',
          },
        ]}
        initialValue={false}
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label="Подтверждать перевод на оператора:"
        name={'askToConnect'}
        valuePropName="checked"
        rules={[
          {
            type: 'boolean',
          },
        ]}
        initialValue={false}
      >
        <Switch />
      </Form.Item>
    </div>
  );
};

export default CampaignSettings;
